package br.com.texeratx.boardgametimeapp.ui.screens.matches.edit

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings
import br.com.texeratx.boardgametimeapp.ui.screens.players.PlayerAvatarMini
import br.com.texeratx.boardgametimeapp.ui.screens.players.model.PlayerUI
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppScreenScaffold

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun MatchEditUI(
    state: MatchEditUiState,

    onBack: () -> Unit,
    onSave: () -> Unit,

    onDateChange: (String) -> Unit,
    onTimeChange: (String) -> Unit,
    onDurationChange: (String) -> Unit,
    onObservationChange: (String) -> Unit,

    onScoreChange: (playerId: Long, value: String) -> Unit,
    onRemovePlayer: (playerId: Long) -> Unit,

    onAddPlayerClick: () -> Unit,
    onAddPlayer: (PlayerUI) -> Unit,
    onCloseAddPlayerDialog: () -> Unit,

    onToggleExpansion: (Long) -> Unit
) {
    val S = LocalStrings.current

    AppScreenScaffold(

        /* =========================
         * TOP BAR
         * ========================= */
        topBar = {
            TopAppBar(
                title = { Text(S.editMatchTitle, fontWeight = FontWeight.Bold) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = S.back)
                    }
                }
            )
        },

        /* =========================
         * BOTTOM BAR — SALVAR
         * ========================= */
        bottomBar = {
            Button(
                onClick = onSave,
                enabled = state.canSave,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp)
                    .height(52.dp)
            ) {
                if (state.isSaving) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(20.dp),
                        strokeWidth = 2.dp
                    )
                } else {
                    Text(S.save, fontWeight = FontWeight.Bold)
                }
            }
        }

    ) { padding ->

        /* =========================
         * CONTENT
         * ========================= */
        LazyColumn(
            modifier = Modifier
                .padding(padding)
                .padding(horizontal = 16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {

            /* ---------- PARTIDA ---------- */

            item {
                Text(S.sectionMatch, fontWeight = FontWeight.Bold)
            }

            item {
                OutlinedTextField(
                    value = state.date,
                    onValueChange = onDateChange,
                    label = { Text(S.labelDate) },
                    modifier = Modifier.fillMaxWidth()
                )
            }

            item {
                OutlinedTextField(
                    value = state.time,
                    onValueChange = onTimeChange,
                    label = { Text(S.labelTimeHHmm) },
                    modifier = Modifier.fillMaxWidth()
                )
            }

            item {
                OutlinedTextField(
                    value = state.duration,
                    onValueChange = onDurationChange,
                    label = { Text(S.labelDurationMin) },
                    modifier = Modifier.fillMaxWidth()
                )
            }

            item {
                OutlinedTextField(
                    value = state.observation.orEmpty(),
                    onValueChange = onObservationChange,
                    label = { Text(S.labelObservation) },
                    modifier = Modifier.fillMaxWidth(),
                    minLines = 2
                )
            }

            /* ---------- JOGADORES ---------- */

            item {
                Text(S.sectionPlayers, fontWeight = FontWeight.Bold)
                TextButton(
                    onClick = onAddPlayerClick
                ) {
                    if (state.isAddPlayerDialogOpen) {
                        AddPlayerDialog(
                            players = state.availablePlayers,
                            onSelect = onAddPlayer,
                            onDismiss = onCloseAddPlayerDialog
                        )
                    }

                    TextButton(onClick = onAddPlayerClick) {
                        Text(S.addPlayerBtn)
                    }
                }

            }

            items(state.players) { player ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {

                    // Avatar
                    PlayerAvatarMini(
                        imageUrl = player.imageUrl,
                        name = player.nickname ?: player.name
                    )

                    // Nome
                    Text(
                        text = player.nickname ?: player.name,
                        modifier = Modifier.weight(1f)
                    )

                    // Pontos (compacto)
                    OutlinedTextField(
                        value = player.points,
                        onValueChange = { value ->
                            onScoreChange(player.playerId, value)
                        },
                        modifier = Modifier
                            .width(56.dp)
                            .height(48.dp),
                        singleLine = true
                    )

                    // Remover
                    IconButton(
                        onClick = { onRemovePlayer(player.playerId) }
                    ) {
                        Icon(
                            imageVector = Icons.Default.Close,
                            contentDescription = S.removePlayer
                        )
                    }
                }
            }

            item {
                Text(S.sectionExpansions, fontWeight = FontWeight.Bold)
            }

            when {
                state.isLoadingExpansions -> {
                    item { Text(S.loadingExpansions) }
                }

                state.availableExpansions.isEmpty() -> {
                    item { Text(S.noExpansionsForGame) }
                }

                else -> {
                    items(state.availableExpansions) { expansion ->
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable {
                                    onToggleExpansion(expansion.id)
                                }
                                .padding(vertical = 2.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {

                            Checkbox(
                                checked = state.selectedExpansionIds.contains(expansion.id),
                                onCheckedChange = {
                                    onToggleExpansion(expansion.id)
                                }
                            )

                            Spacer(Modifier.width(8.dp))

                            Text(expansion.name)
                        }
                    }
                }
            }



            /* Espaço extra para não colar no botão */
            item {
                Spacer(modifier = Modifier.height(40.dp))
            }
        }
    }
}

@Composable
fun AddPlayerDialog(
    players: List<PlayerUI>,
    onSelect: (PlayerUI) -> Unit,
    onDismiss: () -> Unit
) {
    val S = LocalStrings.current
    AlertDialog(
        onDismissRequest = onDismiss,
        confirmButton = {},
        title = { Text(S.dialogAddPlayer) },
        text = {
            LazyColumn {
                items(players) { player ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable { onSelect(player) }
                            .padding(12.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        PlayerAvatarMini(
                            imageUrl = player.imageUrl,
                            name = player.nickname ?: player.name
                        )
                        Spacer(Modifier.width(12.dp))
                        Text(player.nickname ?: player.name)
                    }
                }
            }
        }
    )
}
